import {useCallback} from 'react';
import {ConditionId, UserId} from '@legit.health/ui';
import {useNavigate} from '@tanstack/react-router';
import {createDiagnosticReportOutdatedEvent} from '@/practitioner/hooks/diagnosticReport/api/diagnosticReportOutdatedEvent';
import {useSuspensePatient} from '@/practitioner/hooks/patient/usePatient';
import useCurrentPractitioner from '@/practitioner/hooks/useCurrentPractitioner';
import {
  DIAGNOSTIC_REPORT_RESULT,
  DIAGNOSTIC_REPORT_SENT,
  ENCOUNTER
} from '@/practitioner/routing/paths';
import {CreateDiagnosticReportResponse} from '@/shared/components/diagnosticReport/DiagnosticReportForm';
import DiagnosticReportForm from '@/shared/components/diagnosticReport/DiagnosticReportForm';
import {useEventDispatcher} from '@/shared/contexts/EventDispatcherContext';
import {useCompany} from '@/shared/contexts/UserContext/useUserContext';
import NoScoringSystems from './components/NoScoringSystems';
import VisitIdentifier from './components/VisitIdentifier/VisitIdentifier';

export default function ModalContent({
  patientId,
  conditionId,
  onSuccess
}: {
  patientId: UserId;
  conditionId?: ConditionId | null;
  onSuccess: () => void;
}) {
  const navigate = useNavigate();
  const company = useCompany();
  const practitioner = useCurrentPractitioner();
  const eventDispatcher = useEventDispatcher();
  const patient = useSuspensePatient(patientId);

  const handleSuccess = useCallback(
    function (response: CreateDiagnosticReportResponse) {
      onSuccess();
      if (response.type === 'diagnosticReport') {
        if (response.previousDiagnosticReportId) {
          eventDispatcher.dispatch(
            createDiagnosticReportOutdatedEvent(response.previousDiagnosticReportId)
          );
        }
        if (practitioner.practitionerPermissions.viewDiagnosticReports) {
          navigate({
            to: DIAGNOSTIC_REPORT_RESULT,
            params: {
              patientId,
              diagnosticReportId: response.id
            }
          });
        } else {
          navigate({to: DIAGNOSTIC_REPORT_SENT});
        }
      } else if (!company.isDiagnosticReportResultVisibleToPractitioner) {
        navigate({to: DIAGNOSTIC_REPORT_SENT});
      } else {
        navigate({
          to: ENCOUNTER,
          params: {
            encounterId: response.encounterId,
            patientId
          }
        });
      }
    },
    [
      onSuccess,
      company.isDiagnosticReportResultVisibleToPractitioner,
      practitioner.practitionerPermissions.viewDiagnosticReports,
      eventDispatcher,
      navigate,
      patientId
    ]
  );

  return (
    <DiagnosticReportForm
      patient={patient}
      conditions={patient.conditions}
      onSuccess={handleSuccess}
      conditionId={conditionId}
      noScoringSystems={<NoScoringSystems />}
      visitIdentifierComponent={
        company.isForClinicalTrials && company.clinicalTrial?.enableVisitIdentifier
          ? VisitIdentifier
          : undefined
      }
      withTemporaryImages
    />
  );
}
